import classNames from "classnames";
import { ComponentProps, forwardRef, useEffect, useState } from "react";
import styles from "./StyledInput.module.css";
import InputValidationMessages from "../InputValidationMessages/InputValidationMessages";

export type StyledInputValidationProps =
  | {
      invalid: boolean;
      validationMessages?: never;
    }
  | {
      invalid?: never;
      validationMessages?: string[];
    };

export type StyledInputLabelProps =
  | {
      label: string;
      "aria-label"?: never;
    }
  | {
      label?: Exclude<React.ReactNode, "string">;
      "aria-label": string;
    };

export type StyledInputProps = Omit<
  ComponentProps<"input">,
  "label" | "aria-label"
> & {
  rightContent?: React.ReactNode;
  innerContentClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  showPopover?: boolean;
  variant?: "default" | "horizontal";
};

type Props = StyledInputProps &
  StyledInputLabelProps &
  StyledInputValidationProps;

const ForwardedStyledInput = forwardRef<HTMLInputElement, Props>(
  function StyledInput(
    {
      label,
      "aria-label": ariaLabel,
      className,
      innerContentClassName,
      labelClassName,
      inputClassName,
      disabled,
      rightContent,
      validationMessages,
      invalid = !!validationMessages && validationMessages.length > 0,
      type,
      onFocus,
      showPopover = true,
      autoComplete = type === "password" ? "off" : "new-password",
      variant = "default",
      ...rest
    }: Props,
    ref,
  ) {
    const [hidePopoverOverride, setHidePopoverOverride] = useState(false);

    useEffect(() => {
      setHidePopoverOverride(false);
    }, [validationMessages]);

    return (
      <div className={classNames(styles.container, className)}>
        <label
          className={classNames(
            {
              [styles.label]: true,
              [styles.disabled]: disabled,
              [styles.labelHorizontal]: variant === "horizontal",
              [styles.labelHorizontalError]:
                variant === "horizontal" &&
                !!validationMessages &&
                validationMessages.length > 0,
            },
            innerContentClassName,
          )}
          aria-label={ariaLabel ?? label}
        >
          {label && (
            <span
              className={classNames(
                {
                  [styles.innerLabelHorizontalError]:
                    variant === "horizontal" &&
                    !!validationMessages &&
                    validationMessages.length > 0,
                },
                labelClassName,
              )}
            >
              {label}
            </span>
          )}
          <div
            className={classNames(
              {
                [styles.inputContainer]: true,
                [styles.inputContainerHorizontal]: variant === "horizontal",
              },
              inputClassName,
            )}
          >
            <input
              {...rest}
              ref={ref}
              type={type}
              className={classNames({
                [styles.input]: true,
                [styles.invalid]: invalid,
                [styles.withRightContent]: !!rightContent,
              })}
              disabled={disabled}
              autoComplete={autoComplete}
              onFocus={(e) => {
                onFocus?.(e);
                setHidePopoverOverride(false);
              }}
            />
            <InputValidationMessages
              showPopover={showPopover}
              hidePopoverOverride={hidePopoverOverride}
              setHidePopoverOverride={setHidePopoverOverride}
              validationMessages={validationMessages}
            />
          </div>
          {rightContent}
        </label>
      </div>
    );
  },
);

export default ForwardedStyledInput;
